import styles from "./index.module.css"
import PropTypes from "prop-types"
import BookingConflictWarning from "pagesV2/pro/Calendar/Tooltips/BookingConflictWarning"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import {
  getDefaultCalendarColor,
  getPaymentAccentColor,
  getPrimaryCalendarTextColor,
  resolveBookingColors
} from "pagesV2/pro/Calendar/calendarColorUtils"
import { useTheme } from "context/AntdThemeContext"
import BookingPopover from "pagesV2/pro/Calendar/shared/BookingPopover"
import WaiverBadge from "pagesV2/pro/Calendar/shared/WaiverBadge"
import { getWaiverStatus, getWaiverBadgeLabel, getPendingWaiverCount, isWaiverAttached } from "pagesV2/pro/Calendar/shared/bookingTileUtils"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"

/**
 * One booking in the week or day grid.
 *
 * A bar down the left edge carries the payment state, exactly as the calendar Legend spells it
 * out, while the background carries the experience colour — so the tile answers "which
 * experience" and "is it paid" at a glance.
 *
 * The two grids order the same two lines differently, which is what `layout` picks: the week
 * column leads with the time, because the reader is scanning a day for a slot, and the day grid
 * leads with the trip name, because the time is already given by the block's position.
 */
const EventBlock = ({
  event,
  eventColor,
  conflict,
  isBlackoutConflict,
  setIsBookingModalOpen,
  setSelectedBookingId,
  fullHeight,
  compact,
  hideTime,
  layout = "time-first"
}) => {
  const { id, startLabel, endLabel, title, multi_day, total_guest_count, max_guests } = event || {}
  const { theme } = useTheme()
  const { t } = useTranslation()
  const showWaiverBadge = isWaiverAttached(event)
  const isTitleFirst = layout === "title-first"

  const handleClick = () => {
    setIsBookingModalOpen(true)
    setSelectedBookingId(event?.id)
  }

  const displayEndLabel = multi_day ? null : endLabel
  const timeRange = startLabel && displayEndLabel ? `${startLabel} - ${displayEndLabel}` : startLabel || null

  const primaryTextColor = getPrimaryCalendarTextColor(theme)
  const previewColors = resolveBookingColors(event?.color || eventColor, getDefaultCalendarColor(), primaryTextColor, getEventColor)
  const accentColor = getPaymentAccentColor(event?.payment_status) || previewColors.borderColor

  // Capacity is not on the calendar booking payload yet, so the counter renders only where the
  // experience's `max_guests` happens to come through. See docs/calendar-filters-api.md.
  const guestCount = Number(total_guest_count) || 0
  const capacity = Number(max_guests) || 0
  const guestLabel = guestCount && capacity ? `${guestCount}/${capacity}` : ""

  const blockClassName = [
    styles.eventBlock,
    fullHeight ? styles.eventBlockFull : "",
    compact ? styles.eventBlockCompact : "",
    isBlackoutConflict ? styles.blackoutConflict : ""
  ]
    .filter(Boolean)
    .join(" ")

  /**
   * The week tile leads with the time, so its first line is taken and the counter has to float
   * in the corner. The day grid hides the time — that line is free, so the counter sits beside
   * the name instead of on top of it, which is what made a longer name read as "William Doc1/100".
   */
  const isCounterFloating = !isTitleFirst && !hideTime

  const timeNode =
    !hideTime && timeRange ? (
      <span className={isTitleFirst ? styles.timeSecondary : styles.time}>{timeRange}</span>
    ) : null

  // Sharing a line means the name is the part that yields: the counter is short and fixed, the
  // name is arbitrarily long, so it takes the leftover width and ellipsises inside it.
  const getTitleClassName = () => {
    if (isTitleFirst) return styles.titlePrimary
    return hideTime ? styles.titleFlexible : styles.title
  }
  const titleNode = title ? <span className={getTitleClassName()}>{title}</span> : null
  const guestNode = guestLabel ? (
    <span className={styles.guests}>
      {isTitleFirst ? t("calendar.multi_day.booked", { defaultValue: "{{label}} booked", label: guestLabel }) : guestLabel}
    </span>
  ) : null
  const waiverNode = showWaiverBadge ? (
    <WaiverBadge status={getWaiverStatus(event)} label={getWaiverBadgeLabel(event)} count={getPendingWaiverCount(event)} />
  ) : null

  return (
    <BookingPopover booking={event} colors={previewColors}>
      <button
        type="button"
        key={id || title + startLabel + endLabel}
        className={blockClassName}
        style={{
          // Booking tints are low-alpha and assume a white cell behind them. A blacked-out
          // column is dark or grey, so the tint is painted as a layer over an opaque white
          // base — the tile stays readable whatever form the colour takes.
          backgroundImage: `linear-gradient(${previewColors.backgroundColor}, ${previewColors.backgroundColor})`,
          ...(fullHeight ? { height: "100%" } : {})
        }}
        onClick={handleClick}
      >
        <span className={styles.accent} style={{ backgroundColor: accentColor }} aria-hidden />
        <span className={styles.body}>
          {isTitleFirst ? (
            <>
              <span className={styles.line}>
                {isBlackoutConflict && <Icon name="AlertTriangle" width={14} height={14} color="var(--color-error)" />}
                {titleNode}
              </span>
              {timeNode}
              {/* The design pairs the capacity counter and the waiver chip on one row — both are
                  short, and the tile has no height to spend on a line each. */}
              {(guestNode || waiverNode) && (
                <span className={styles.metaRow}>
                  {guestNode}
                  {waiverNode}
                </span>
              )}
            </>
          ) : (
            <>
              <span className={styles.line}>
                {isBlackoutConflict && <Icon name="AlertTriangle" width={14} height={14} color="var(--color-error)" />}
                {hideTime ? titleNode : timeNode}
                {!isCounterFloating && guestNode}
              </span>
              {!hideTime && titleNode}
            </>
          )}
          {!isTitleFirst && waiverNode}
          {conflict ? <BookingConflictWarning /> : null}
        </span>
        {/* The week tile is 44px and has no room for a third line, so its counter floats. */}
        {isCounterFloating && guestNode}
      </button>
    </BookingPopover>
  )
}

EventBlock.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startLabel: PropTypes.string,
    endLabel: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    payment_status: PropTypes.string,
    total_guest_count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    max_guests: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    multi_day: PropTypes.bool
  }),
  eventColor: PropTypes.string,
  setIsBookingModalOpen: PropTypes.func,
  setSelectedBookingId: PropTypes.func,
  conflict: PropTypes.bool,
  /** The booking falls inside a blackout and needs cancelling or rescheduling. */
  isBlackoutConflict: PropTypes.bool,
  fullHeight: PropTypes.bool,
  compact: PropTypes.bool,
  hideTime: PropTypes.bool,
  /** "time-first" for the week column, "title-first" for the day grid. */
  layout: PropTypes.oneOf(["time-first", "title-first"])
}

export default EventBlock
